import { TireHotelLocationRecord } from '@navio/nsdk';
import { URLKeys, noddiAsync } from 'noddi-async';
import { ApiErrorMessageInternal, NoddiAsyncSelect, usePagination } from 'noddi-ui-web';

type Props = {
  label?: string;
  selectedLocation?: TireHotelLocationRecord;
  onLocationChange: (location: TireHotelLocationRecord | undefined) => void;
  placeholder?: string;
  // Keeping this as not optional to ensure the caller always provides it ( if they have it ), which makes sense in BF.
  validForAddressId: number | undefined;
};

export const TireHotelLocationsSelect = ({
  label,
  selectedLocation,
  onLocationChange,
  placeholder,
  validForAddressId
}: Props) => {
  const { pageIndex, pageSize, search, setSearch } = usePagination();

  const {
    data: tireHotelLocations,
    isPending,
    error: tireHotelLocationsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedTireHotelLocations,
    input: {
      pageIndex,
      pageSize,
      search,
      // Should be renamed to validForAddressIds in the API..
      validForAddressId: validForAddressId !== undefined ? [validForAddressId] : undefined
    }
  });

  if (tireHotelLocationsError) {
    return <ApiErrorMessageInternal error={tireHotelLocationsError} />;
  }

  const locationOptions =
    tireHotelLocations?.results.map((location) => ({
      label: `${location.name} - ${location.address.streetName} ${location.address.streetNumber}, ${location.address.city}`,
      value: location.id
    })) || [];

  const selectedOption = selectedLocation
    ? {
        label: `${selectedLocation.name} - ${selectedLocation.address.streetName} ${selectedLocation.address.streetNumber}, ${selectedLocation.address.city}`,
        value: selectedLocation.id
      }
    : undefined;

  return (
    <NoddiAsyncSelect
      label={label}
      isLoading={isPending}
      options={locationOptions}
      onSearch={(inputValue) => {
        setSearch(inputValue);
      }}
      selectedOption={selectedOption}
      onSelectionChange={(option) => {
        if (option) {
          const location = tireHotelLocations?.results.find((loc) => loc.id === option.value);
          onLocationChange(location);
        } else {
          onLocationChange(undefined);
        }
      }}
      placeholder={placeholder}
      // Names carry the full address, which a trigger-narrow list cuts off.
      popoverClassName='w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)]'
    />
  );
};
